import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CloseIcon from "@mui/icons-material/Close";
import call from "../../Api/call";
import color from "../../Constants/colors";
import { useLibraryDocuments, useLibraryQuota } from "../../Query/useComoAgent";
import { LibraryDocument, LibraryKind } from "../../Models/comoAgent";
import { downloadDocumentBlob } from "../../Helpers/documentHelper";
import {
  getFileTypeStyle,
  getFileExtension,
} from "../../Helpers/fileTypePresentation";
import { formatDate } from "../../Helpers/dateHelpers";
import PdfPreviewModal from "../Common/PdfPreviewModal";
import ImagePreviewModal from "../Common/ImagePreviewModal";
import { useTranslation } from "react-i18next";

const PAGE_SIZE = 50;
const IMAGE_EXTENSIONS = new Set(["png", "jpg", "jpeg", "gif", "webp"]);

const lightSelectSx = {
  bgcolor: "white",
  fontSize: "0.875rem",
  borderRadius: 2,
  color: `${color.primary_dark} !important`,
  "& .MuiSelect-select": {
    color: `${color.primary_dark} !important`,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: `${color.gray300} !important`,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: `${color.gray300} !important`,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: `${color.gray300} !important`,
  },
};

const lightMenuProps = {
  PaperProps: {
    sx: {
      bgcolor: `${color.white} !important`,
      border: `1px solid ${color.gray200}`,
      boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
      "& .MuiMenuItem-root": {
        color: `${color.primary_dark} !important`,
        fontSize: "0.875rem",
        "&:hover": {
          backgroundColor: `${color.gray50} !important`,
        },
        "&.Mui-selected": {
          backgroundColor: `${color.primary_dark}0F !important`,
          color: `${color.primary_dark} !important`,
        },
        "&.Mui-selected:hover": {
          backgroundColor: `${color.primary_dark}1A !important`,
        },
      },
    },
  },
};

const downloadUrl = (doc: LibraryDocument): string => {
  if (doc.item_type === "asset") {
    return `/v1/agent_chat/${doc.conversation_id}/assets/${doc.id}/download/`;
  }
  if (doc.item_type === "image") {
    return `/v1/agent_chat/${doc.conversation_id}/images/${doc.id}/${doc.image_index}/download/`;
  }
  return `/v1/agent_chat/${doc.conversation_id}/documents/${doc.id}/download/`;
};

const itemKey = (doc: LibraryDocument): string =>
  doc.item_type === "image"
    ? `image-${doc.id}-${doc.image_index}`
    : `${doc.item_type ?? "document"}-${doc.id}`;

const isPdf = (doc: LibraryDocument): boolean =>
  getFileExtension(doc.filename) === "pdf";

const isImage = (doc: LibraryDocument): boolean =>
  IMAGE_EXTENSIONS.has(getFileExtension(doc.filename));

const isPreviewable = (doc: LibraryDocument): boolean =>
  isPdf(doc) || isImage(doc);

interface PreviewState {
  type: "pdf" | "image";
  url: string;
  name: string;
}

interface LibraryViewProps {
  onOpenConversation?: (conversationId: number) => void;
  onClose?: () => void;
}

const iconBtnSx = {
  color: color.gray500,
  "&:hover": { bgcolor: color.gray100, color: color.accent },
};

const LibraryView: React.FC<LibraryViewProps> = ({
  onOpenConversation,
  onClose,
}) => {
  const { t } = useTranslation("comoExperts");
  const [kind, setKind] = useState<LibraryKind>("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [fileType, setFileType] = useState("all");

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const { data, isLoading, isError } = useLibraryDocuments({
    kind,
    search,
    page: 1,
    pageSize: PAGE_SIZE,
  });

  const { data: quota } = useLibraryQuota();

  const hasLimit = !!quota && quota.limit !== -1 && quota.limit > 0;
  const isOverLimit = hasLimit && quota!.consumed >= quota!.limit;

  const usagePercentage = hasLimit
    ? Math.min((quota!.consumed / quota!.limit) * 100, 100)
    : 0;

  const documents = useMemo<LibraryDocument[]>(
    () => data?.documents ?? [],
    [data],
  );

  const filteredDocuments = useMemo(() => {
    return documents.filter((doc) => {
      const extension = getFileExtension(doc.filename).toLowerCase();

      const matchesSearch =
        searchInput.trim() === "" ||
        doc.filename.toLowerCase().includes(searchInput.toLowerCase()) ||
        doc.conversation_title
          ?.toLowerCase()
          .includes(searchInput.toLowerCase());

      const matchesType =
        fileType === "all" ||
        (fileType === "image" && IMAGE_EXTENSIONS.has(extension)) ||
        extension === fileType;

      return matchesSearch && matchesType;
    });
  }, [documents, searchInput, fileType]);

  const handleDownload = async (doc: LibraryDocument) => {
    if (downloadingId !== null) return;
    setDownloadingId(itemKey(doc));
    try {
      const response = await call.get(downloadUrl(doc), {
        responseType: "blob",
      });
      downloadDocumentBlob(response, doc.filename);
    } catch (err) {
      console.error("Failed to download library document", err);
    } finally {
      setDownloadingId(null);
    }
  };

  const handlePreview = (doc: LibraryDocument) => {
    setPreview({
      type: isPdf(doc) ? "pdf" : "image",
      url: downloadUrl(doc),
      name: doc.filename,
    });
  };

  return (
    <Box
      sx={{
        maxWidth: 960,
        mx: "auto",
        px: { xs: 2, md: 4 },
        py: 4,
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
          mb: 3,
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, color: color.primary_dark }}
        >
          {t("comoExperts.libraryTitle")}
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={fileType}
              onChange={(e) => setFileType(e.target.value)}
              sx={lightSelectSx}
              MenuProps={lightMenuProps}
              displayEmpty
            >
              <MenuItem value="all">
                {t("comoExperts.libraryAllFiles")}
              </MenuItem>
              <MenuItem value="pdf">PDF</MenuItem>
              <MenuItem value="docx">DOCX</MenuItem>
              <MenuItem value="pptx">PPTX</MenuItem>
              <MenuItem value="txt">TXT</MenuItem>
              <MenuItem value="image">Images</MenuItem>
            </Select>
          </FormControl>

          <TextField
            size="small"
            placeholder={t("comoExperts.librarySearch")}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: color.gray500, fontSize: 18 }} />
                </InputAdornment>
              ),
              sx: {
                borderRadius: "10px",
                fontSize: "0.85rem",
                bgcolor: color.gray50,
                color: color.primary_dark,
                "& input": { color: color.primary_dark },
                "& input::placeholder": { color: color.gray400, opacity: 1 },
                "& fieldset": { borderColor: color.gray200 },
                "&:hover fieldset": { borderColor: color.gray300 },
                "&.Mui-focused fieldset": {
                  borderColor: color.accent,
                  borderWidth: "1px",
                },
              },
            }}
          />

          {onClose && (
            <IconButton
              onClick={onClose}
              sx={iconBtnSx}
              aria-label={t("comoExperts.libraryClose")}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      {hasLimit && (
        <Box sx={{ mb: 2 }}>
          {isOverLimit && (
            <Alert
              severity="warning"
              sx={{
                borderRadius: 2,
                mb: 1.5,
              }}
            >
              {t("comoExperts.libraryLimitReached", {
                consumed: quota!.consumed,
                limit: quota!.limit,
              })}
            </Alert>
          )}
          <Typography
            variant="body2"
            sx={{
              mb: 0.5,
              color: color.gray700,
            }}
          >
            {t("comoExperts.libraryUsage", {
              consumed: quota!.consumed,
              limit: quota!.limit,
            })}
          </Typography>

          <LinearProgress
            variant="determinate"
            value={usagePercentage}
            sx={{
              width: { xs: "100%", sm: "50%" },
              height: 6,
              borderRadius: 3,
              backgroundColor: color.gray200,
              "& .MuiLinearProgress-bar": {
                backgroundColor: isOverLimit ? color.red : color.accent,
                borderRadius: 3,
              },
            }}
          />
        </Box>
      )}

      <Tabs
        value={kind}
        onChange={(_, value: LibraryKind) => setKind(value)}
        TabIndicatorProps={{
          sx: { backgroundColor: color.accent, height: 2.5 },
        }}
        sx={{
          mb: 2,
          minHeight: 40,
          "& .MuiTab-root": {
            minHeight: 40,
            textTransform: "none",
            fontWeight: 600,
            color: color.gray500,
          },
          "& .Mui-selected": { color: `${color.primary_dark} !important` },
        }}
      >
        <Tab value="all" label={t("comoExperts.libraryAll")} />
        <Tab value="uploaded" label={t("comoExperts.libraryUploaded")} />
        <Tab value="generated" label={t("comoExperts.libraryGenerated")} />
      </Tabs>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr auto auto",
          alignItems: "center",
          px: 2,
          py: 1,
          gap: 2,
          borderBottom: `1px solid ${color.gray200}`,
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: color.gray500, fontWeight: 600 }}
        >
          {t("comoExperts.libraryName")}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: color.gray500, fontWeight: 600 }}
        >
          {t("comoExperts.libraryModified")}
        </Typography>
        <Box sx={{ width: 88 }} />
      </Box>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: color.accent }} />
        </Box>
      ) : isError ? (
        <Typography sx={{ color: color.gray500, textAlign: "center", py: 8 }}>
          {t("comoExperts.libraryLoadError")}
        </Typography>
      ) : filteredDocuments.length === 0 ? (
        <Typography sx={{ color: color.gray500, textAlign: "center", py: 8 }}>
          {documents.length === 0
            ? t("comoExperts.libraryEmpty")
            : t("comoExperts.libraryNoMatchingDocuments")}
        </Typography>
      ) : (
        filteredDocuments.map((doc) => {
          const { Icon, iconColor, iconBg } = getFileTypeStyle(doc.filename);
          return (
            <Box
              key={itemKey(doc)}
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr auto auto",
                alignItems: "center",
                px: 2,
                py: 1.25,
                gap: 2,
                borderBottom: `1px solid ${color.gray100}`,
                "&:hover": { backgroundColor: color.gray50 },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: "9px",
                    bgcolor: iconBg,
                    color: iconColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Icon sx={{ fontSize: 19 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: color.primary_dark,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {doc.filename}
                    </Typography>
                    {doc.is_generated && (
                      <Chip
                        size="small"
                        icon={
                          <AutoAwesomeIcon
                            sx={{
                              fontSize: 13,
                              color: `${color.accent_dark} !important`,
                            }}
                          />
                        }
                        label="Generated"
                        sx={{
                          height: 20,
                          fontSize: "0.68rem",
                          fontWeight: 600,
                          color: color.accent_dark,
                          bgcolor: color.backgroungOfMyQuestionTable,
                          "& .MuiChip-icon": { ml: "4px" },
                        }}
                      />
                    )}
                  </Box>
                  {doc.conversation_title && (
                    <Box
                      onClick={() => onOpenConversation?.(doc.conversation_id)}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        mt: 0.25,
                        cursor: onOpenConversation ? "pointer" : "default",
                        color: color.gray500,
                        "&:hover": onOpenConversation
                          ? { color: color.accent }
                          : undefined,
                      }}
                    >
                      <ChatBubbleOutlineIcon sx={{ fontSize: 13 }} />
                      <Typography
                        variant="caption"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: 420,
                        }}
                      >
                        {doc.conversation_title}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              <Typography
                variant="caption"
                sx={{ color: color.gray500, whiteSpace: "nowrap" }}
              >
                {formatDate(doc.created_at)}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  gap: 0.5,
                  width: 88,
                  justifyContent: "flex-end",
                }}
              >
                {isPreviewable(doc) && (
                  <Tooltip title="Preview">
                    <IconButton
                      size="small"
                      onClick={() => handlePreview(doc)}
                      sx={iconBtnSx}
                    >
                      <VisibilityIcon sx={{ fontSize: 19 }} />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title={t("comoExperts.libraryDownload")}>
                  <IconButton
                    size="small"
                    disabled={downloadingId === itemKey(doc)}
                    onClick={() => handleDownload(doc)}
                    sx={iconBtnSx}
                  >
                    {downloadingId === itemKey(doc) ? (
                      <CircularProgress
                        size={18}
                        sx={{ color: color.accent }}
                      />
                    ) : (
                      <DownloadIcon sx={{ fontSize: 19 }} />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          );
        })
      )}

      {data && data.total > documents.length && (
        <Typography
          variant="caption"
          sx={{
            color: color.gray400,
            display: "block",
            textAlign: "center",
            py: 2,
          }}
        >
          {t("libraryShowing", {
            current: documents.length,
            total: data.total,
          })}
        </Typography>
      )}

      <PdfPreviewModal
        open={preview?.type === "pdf"}
        onClose={() => setPreview(null)}
        url={preview?.type === "pdf" ? preview.url : undefined}
        documentName={preview?.type === "pdf" ? preview.name : undefined}
      />
      <ImagePreviewModal
        open={preview?.type === "image"}
        onClose={() => setPreview(null)}
        url={preview?.type === "image" ? preview.url : undefined}
        documentName={preview?.type === "image" ? preview.name : undefined}
      />
    </Box>
  );
};

export default LibraryView;
